import type { UseFormReturn } from "react-hook-form";
import {
  Checkbox,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Textarea,
  TextInput,
  Typography,
} from "@/components/ui";
import { RepairIntakeRadioField } from "@/pages/public/repairIntakeForm/RepairIntakeRadioField";
import { desiredRepairLifeOptions } from "@/pages/public/repairIntakeForm/repairIntakeFormConstants";
import type { RepairIntakeFormData } from "@/store/useRepairIntakeStore";

type RepairIntakeSection7Props = {
  form: UseFormReturn<RepairIntakeFormData>;
  desiredRepairLife: string;
  goodPipeOnLandingZones: RepairIntakeFormData["section7"]["goodPipeOnLandingZones"];
  onUpdateDesiredRepairLife: (value: string) => void;
};

export function RepairIntakeSection7({
  form,
  desiredRepairLife,
  goodPipeOnLandingZones,
  onUpdateDesiredRepairLife,
}: RepairIntakeSection7Props) {
  return (
    <>
      <RepairIntakeRadioField
        form={form}
        name="section7.installedUnderPressure"
        label="1. Will repair be installed under pressure (live line)? *"
      />

      <div className="space-y-3">
        <Typography variant="text-small" weight="medium">
          2. Desired Repair Life? *
        </Typography>
        <div className="grid gap-3 sm:grid-cols-2">
          {desiredRepairLifeOptions.map((option) => (
            <Checkbox
              key={option}
              checked={desiredRepairLife === option}
              label={option === "Other" ? "Other:" : option}
              onChange={() => onUpdateDesiredRepairLife(option)}
            />
          ))}
        </div>
        {desiredRepairLife === "Other" ? (
          <FormField
            control={form.control}
            name="section7.desiredRepairLifeOther"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TextInput placeholder="Other desired repair life" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <Typography variant="validationError">{form.formState.errors.section7?.desiredRepairLife?.message}</Typography>
      </div>

      <RepairIntakeRadioField
        form={form}
        name="section7.goodPipeOnLandingZones"
        label="3. Is 12 inches of good pipe on all landing zones? *"
      />

      {goodPipeOnLandingZones === "No" ? (
        <FormField
          control={form.control}
          name="section7.inchesOfGoodPipe"
          render={({ field }) => (
            <FormItem>
              <FormLabel>4. If no, how many inches of good pipe do we have to land on? *</FormLabel>
              <FormControl>
                <TextInput {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}

      <RepairIntakeRadioField
        form={form}
        name="section7.defectsFieldVerified"
        label="5. Have all defect details been field verified? *"
        helperText="All Jobs must be field verified before submitting"
      />

      <FormField
        control={form.control}
        name="section7.additionalNotes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>6. Additional Notes *</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormDescription>
              Provide any notes you want engineering to know that is not covered in the previous sections here, if no
              extra notes put NA
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
